import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setAuthToken, checkAuth, checkSetupRequired, login as apiLogin, register as apiRegister } from '../api/client';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  setupRequired: boolean;
  userId: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState>(null!);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      const needsSetup = await checkSetupRequired();
      setSetupRequired(needsSetup);

      const token = localStorage.getItem('auth_token');
      if (token) {
        setAuthToken(token);
        const result = await checkAuth();
        if (result.ok) {
          setIsAuthenticated(true);
          setUserId(result.id || null);
        } else {
          localStorage.removeItem('auth_token');
          setAuthToken(null);
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const handleAuth = useCallback(async (token: string) => {
    localStorage.setItem('auth_token', token);
    setAuthToken(token);
    setIsAuthenticated(true);
    setSetupRequired(false);
    const result = await checkAuth();
    if (result.ok) setUserId(result.id || null);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const { token } = await apiLogin(username, password);
    await handleAuth(token);
  }, [handleAuth]);

  const register = useCallback(async (username: string, password: string) => {
    const { token } = await apiRegister(username, password);
    await handleAuth(token);
  }, [handleAuth]);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setAuthToken(null);
    setIsAuthenticated(false);
    setUserId(null);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, setupRequired, userId, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
